import { Component, OnInit, inject, DestroyRef, effect } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Title } from '@angular/platform-browser';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { NavbarComponent } from './layout/navbar/navbar.component';
import { NavtopComponent } from './layout/navtop/navtop.component';
import { FooterComponent } from './layout/footer/footer.component';
import { PageHeaderComponent } from './components/page-header/page-header.component';

import { LoadingService } from './services/loading/loading.service';
import { ConfigService } from './services/config/config.service';
import { ScoringRealtimeService, ScorecardActivityData } from './services/signalr/scoring-realtime.service';
import { AuthService } from './services/auth/auth.service';
import { LoggingService } from './services/shared/logging/logging.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    NavbarComponent,
    NavtopComponent,
    RouterOutlet,
    FooterComponent,
    PageHeaderComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {
  loading$ = this.loadingService.loading$;

  private readonly scoringRealtimeService = inject(ScoringRealtimeService);
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);
  private scorecardActivitySubscriptionSetup = false;

  constructor(
    private loadingService: LoadingService,
    private configService: ConfigService,
    private titleService: Title,
    public router: Router,
    private loggingService: LoggingService
  ) {
    // Make app component available in console for debugging
    (window as any).app = this;

    // Watch for authentication state changes and refresh connection using signals
    effect(async () => {
      const isAuthenticated = this.authService.authenticationStateChange();

      if (isAuthenticated) {
        try {
          // Disconnect current connection first
          await this.scoringRealtimeService.refreshConnection();

          // Reinitialize with fresh token factory that has authentication
          const tokenFactory = () => {
            const token = this.authService.getAccessToken();
            return token || '';
          };

          await this.scoringRealtimeService.reinitialize(tokenFactory);

          // Re-setup subscriptions after reinitialization (only if not already set up)
          this.initializeScoringRealtimeService();
        } catch (error) {
          this.loggingService.error('Failed to reinitialize SignalR service after login', 'AppComponent', error);
        }
      }
    });
  }

  get showFooter(): boolean {
    // Hide footer on any login route (including query params)
    return !this.router.url.startsWith('/login');
  }

  ngOnInit() {
    // Load the configuration on initialization
    const title = this.configService.config?.title || 'R2IT Golf Scoring';
    this.titleService.setTitle(title);

    // Connect SignalR first, then set up subscriptions
    this.connectSignalR().then(() => {
      // Set up scorecard activity subscription after connection is established
      this.initializeScoringRealtimeService();
    });
  }

  /**
   * Initialize the scoring realtime service for scorecard-specific events
   * Components will handle their own joining/leaving of groups
   */
  private initializeScoringRealtimeService(): void {
    // Prevent multiple subscriptions
    if (this.scorecardActivitySubscriptionSetup) {
      this.loggingService.debug('Scorecard activity subscription already set up, skipping...', 'AppComponent');
      return;
    }

    this.loggingService.debug('Setting up scorecard activity subscription in app component...', 'AppComponent');
    this.loggingService.debug(`Service is initialized: ${this.scoringRealtimeService.isInitialized()}`, 'AppComponent');
    this.loggingService.debug(`Service is connected: ${this.scoringRealtimeService.isConnected()}`, 'AppComponent');

    // Listen for scorecard activity broadcasts from the service
    this.scoringRealtimeService.scorecardActivity
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: ScorecardActivityData) => {
          this.loggingService.debug(`Scorecard activity broadcast received in app component: ${data.type} for scorecard ${data.scorecardId} at ${data.timestamp.toLocaleTimeString()} (Tournament: ${data.tournamentId}, Round: ${data.roundId}, Player: ${data.playerId})`, 'AppComponent');
          // Handle any global scorecard activity notifications here
          // Most specific handling should be done in individual components
        },
        error: (error) => {
          this.loggingService.error('Error in scorecard activity subscription', 'AppComponent', error);
        },
        complete: () => {
          this.loggingService.debug('Scorecard activity subscription completed', 'AppComponent');
        }
      });

    this.scorecardActivitySubscriptionSetup = true;
    this.loggingService.debug('Scorecard activity subscription setup complete', 'AppComponent');
    this.loggingService.debug('Subscription is now active and listening...', 'AppComponent');
  }

  /**
   * Connect to SignalR hub on app startup
   * Uses anonymous connection to allow access without authentication
   */
  async connectSignalR(): Promise<void> {
    try {
      if (!this.scoringRealtimeService.isConnected()) {
        // Use anonymous connection - no token required
        await this.scoringRealtimeService.initializeWithEnvironment();
      }
    } catch (error) {
      this.loggingService.error('Failed to connect ScoringRealtimeService', 'AppComponent', error);
    }
  }

  /**
   * Disconnect from ScoringRealtimeService before logout
   * This method should be called before logout
   */
  async disconnectSignalR(): Promise<void> {
    try {
      if (this.scoringRealtimeService.isConnected()) {
        // Components will handle their own cleanup, just disconnect the main services
        await this.scoringRealtimeService.disconnect();
        this.loggingService.debug('ScoringRealtimeService disconnected', 'AppComponent');
      }
    } catch (error) {
      this.loggingService.error('Failed to disconnect ScoringRealtimeService before logout', 'AppComponent', error);
    }
  }

  /**
   * Diagnostic method to test SignalR connection manually
   */
  async testSignalRConnection(): Promise<void> {
    this.loggingService.debug('Running SignalR diagnostic test...', 'AppComponent');

    try {
      // Test WebSocket connectivity first
      const webSocketTest = await this.scoringRealtimeService.testAndEnableWebSockets();

      if (this.scoringRealtimeService.isConnected()) {
        const transportInfo = this.scoringRealtimeService.getTransportInfo();
        this.loggingService.debug(`SignalR is connected using ${transportInfo.transport} ${transportInfo.isOptimal ? '(optimal)' : '(fallback)'}`, 'AppComponent');

        // Test connection info
        const connectionInfo = this.scoringRealtimeService.getConnectionInfo();
        this.loggingService.debug(`Connection details: ${JSON.stringify(connectionInfo())}`, 'AppComponent');
      } else {
        this.loggingService.debug('SignalR is not connected', 'AppComponent');
      }
    } catch (error) {
      this.loggingService.error('SignalR diagnostic test failed', 'AppComponent', error);
    }
  }

  /**
   * Test method to trigger manual scorecard activity broadcast
   * Call this from browser console: window.app.testScorecardBroadcast()
   */
  testScorecardBroadcast(): void {
    this.loggingService.debug('Testing scorecard activity broadcast from app component...', 'AppComponent');
    (this.scoringRealtimeService as any).testBroadcast();
  }
}
